// ============================================================================
// PRESS FOLDER ALERTS HOOK
// ============================================================================
// Reads the open rows of comms.press_folder_alerts so RADAR can surface them.
// The Dropbox webhook (supabase/functions/dropbox-webhook) writes that table
// whenever Dropbox reports that something in the press tree moved; nothing here
// recomputes anything.
//
// "Open" is the only state worth showing. A finding the next scan no longer
// sees resolves itself, and one somebody dismissed from the Comms page is gone
// for good — so filtering on status here is what keeps RADAR and the Comms
// panel agreeing without either having to tell the other anything.
//
// Reads fail closed: the table is readable by any authenticated user, but a
// signed-out or errored read yields an empty list and the rule produces
// nothing, rather than breaking the dashboard.
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

export function usePressFolderAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .schema('comms')
        .from('press_folder_alerts')
        .select('id, project_code, kind, path, label, detail, detected_at, last_seen_at')
        .eq('status', 'open')
        .order('detected_at');

      if (err) throw err;

      setAlerts((data || []).map(row => ({
        id:          row.id,
        projectCode: row.project_code,
        kind:        row.kind,
        path:        row.path,
        label:       row.label || '',
        detail:      row.detail || {},
        detectedAt:  row.detected_at,
        lastSeenAt:  row.last_seen_at,
      })));
      setError(null);
    } catch (err) {
      console.error('[PRESS FOLDERS] fetchAlerts error:', err);
      setAlerts([]);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  return { alerts, loading, error, refresh: fetchAlerts };
}

export default usePressFolderAlerts;
